import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowUpRight, ArrowLeft, ArrowRight, ExternalLink, Check } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useCMS } from '../context/CMSContext';
import { successStories as defaultStories } from '../data/mockData';

const Section = ({ eyebrow, title, children, testid }) => (
  <section className="relative py-14 lg:py-20 border-t border-white/5" data-testid={testid}>
    <div className="max-w-6xl mx-auto px-6 lg:px-8 grid lg:grid-cols-12 gap-8">
      <div className="lg:col-span-4">
        {eyebrow && <p className="text-[11px] uppercase tracking-[0.25em] text-white/40 font-bold mb-4">{eyebrow}</p>}
        {title && <h2 className="font-display text-2xl md:text-3xl lg:text-4xl font-medium tracking-tight text-white leading-tight">{title}</h2>}
      </div>
      <div className="lg:col-span-8 text-white/65 text-base lg:text-lg font-light leading-relaxed space-y-4">
        {children}
      </div>
    </div>
  </section>
);

const Metric = ({ v, l }) => (
  <div className="p-6 rounded-2xl border border-white/8 bg-white/[0.015]">
    <p className="font-display text-3xl lg:text-4xl text-white tracking-tight font-medium">{v || '—'}</p>
    <p className="text-[11px] uppercase tracking-[0.2em] text-white/45 mt-2 font-bold">{l}</p>
  </div>
);

const CaseStudyPage = () => {
  const { slug } = useParams();
  const { get } = useCMS();
  const stories = get('success_stories') || defaultStories;
  const story = stories.find((s) => s.slug === slug);

  if (!story) return <Navigate to="/success-stories" replace />;

  const currentIndex = stories.findIndex((s) => s.slug === slug);
  const next = stories[(currentIndex + 1) % stories.length];

  return (
    <div className="min-h-screen bg-[#050914]">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 lg:pt-40" data-testid="case-study-hero">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Link to="/success-stories" className="inline-flex items-center gap-2 text-white/50 hover:text-white text-sm mb-10 transition-colors" data-testid="case-study-back">
            <ArrowLeft className="w-4 h-4" /> All success stories
          </Link>
          <div className="grid lg:grid-cols-12 gap-8 lg:gap-14 mb-14">
            <div className="lg:col-span-8">
              <p className="inline-flex items-center gap-3 text-xs tracking-[0.25em] uppercase text-white/50 font-bold mb-6">
                <span className="w-8 h-px bg-white/30" /> {story.industry}
              </p>
              <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
                className="font-display text-4xl md:text-5xl lg:text-6xl font-medium tracking-tighter text-white leading-[1.02]">
                {story.title}
              </motion.h1>
              <p className="mt-6 text-white/60 text-lg font-light leading-relaxed max-w-2xl">{story.summary}</p>
            </div>
            <div className="lg:col-span-4 flex flex-col gap-3">
              {story.liveUrl && (
                <a href={story.liveUrl} target="_blank" rel="noopener noreferrer"
                  className="group inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-[#FF6B35] text-white font-semibold hover:scale-[1.03] active:scale-[0.97] transition-transform duration-200"
                  data-testid="case-study-live-link">
                  <ExternalLink className="w-4 h-4" />
                  View Live Project
                </a>
              )}
              <Link to="/contact"
                className="group inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-white text-[#050914] font-semibold hover:scale-[1.03] active:scale-[0.97] transition-transform duration-200">
                Book a Consultation <ArrowUpRight className="w-4 h-4 group-hover:rotate-45 transition-transform" />
              </Link>
              {story.liveUrl && (
                <p className="text-xs text-white/40 text-center font-light mt-1">
                  Live link opens the working project preview in a new tab.
                </p>
              )}
            </div>
          </div>

          {/* Big project image */}
          <motion.div initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8 }}
            className="relative overflow-hidden rounded-2xl border border-white/10">
            {story.liveUrl ? (
              <a href={story.liveUrl} target="_blank" rel="noopener noreferrer" className="block group" data-testid="case-study-image-link"
                aria-label="Open live project in a new tab">
                <div className="aspect-[16/8]">
                  <img src={story.image} alt={`${story.client} — ${story.title}`}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1500ms] ease-out group-hover:scale-[1.02]" />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-[#050914]/40 to-transparent" />
                <div className="absolute bottom-6 right-6 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#050914]/80 text-white text-xs font-semibold backdrop-blur border border-white/15">
                  <ExternalLink className="w-3.5 h-3.5" /> Open live project (new tab)
                </div>
              </a>
            ) : (
              <div className="aspect-[16/8]">
                <img src={story.image} alt={`${story.client} — ${story.title}`}
                  className="absolute inset-0 w-full h-full object-cover" />
              </div>
            )}
          </motion.div>

          {/* Meta row */}
          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-5 rounded-2xl border border-white/8">
              <p className="text-[11px] uppercase tracking-[0.25em] text-white/40 font-bold">Client</p>
              <p className="mt-2 text-white text-base">{story.client}</p>
            </div>
            <div className="p-5 rounded-2xl border border-white/8">
              <p className="text-[11px] uppercase tracking-[0.25em] text-white/40 font-bold">Industry</p>
              <p className="mt-2 text-white text-base">{story.industry}</p>
            </div>
            <div className="p-5 rounded-2xl border border-white/8">
              <p className="text-[11px] uppercase tracking-[0.25em] text-white/40 font-bold">Services</p>
              <p className="mt-2 text-white text-sm font-light">{(story.services || []).join(', ')}</p>
            </div>
            <div className="p-5 rounded-2xl border border-white/8">
              <p className="text-[11px] uppercase tracking-[0.25em] text-white/40 font-bold">Key result</p>
              <p className="mt-2 text-white text-sm font-light">{story.keyResult || 'Verified outcomes pending'}</p>
            </div>
          </div>
        </div>
      </section>

      {story.overview && <Section eyebrow="Overview" title="Project overview" testid="cs-overview"><p>{story.overview}</p></Section>}
      {story.challenge && <Section eyebrow="Challenge" title="The client challenge" testid="cs-challenge"><p>{story.challenge}</p></Section>}
      {story.strategy && <Section eyebrow="Strategy" title="Our approach" testid="cs-strategy"><p>{story.strategy}</p></Section>}
      {story.design && <Section eyebrow="Design" title="Design approach" testid="cs-design"><p>{story.design}</p></Section>}
      {story.development && <Section eyebrow="Development" title="Development approach" testid="cs-dev"><p>{story.development}</p></Section>}

      {story.features && story.features.length > 0 && (
        <Section eyebrow="Features" title="Main platform features" testid="cs-features">
          <ul className="grid sm:grid-cols-2 gap-3">
            {story.features.map((f) => (
              <li key={f} className="flex items-start gap-3 p-4 rounded-xl border border-white/8">
                <Check className="w-4 h-4 text-[#FF6B35] mt-1 flex-shrink-0" />
                <span className="text-white/80 text-sm">{f}</span>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {/* Metrics / outcomes */}
      <Section eyebrow="Outcomes" title="Results & intended outcomes" testid="cs-results">
        <p>{story.results || 'Verified performance metrics are added as they are validated with the client.'}</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2">
          <Metric v={story.metric1Value} l={story.metric1Label} />
          <Metric v={story.metric2Value} l={story.metric2Label} />
          <Metric v={story.metric3Value} l={story.metric3Label} />
        </div>
      </Section>

      {story.tech && story.tech.length > 0 && (
        <Section eyebrow="Technology" title="Technologies used" testid="cs-tech">
          <div className="flex flex-wrap gap-2">
            {story.tech.map((t) => (
              <span key={t} className="px-3 py-1 rounded-full text-xs text-white/70 border border-white/15">{t}</span>
            ))}
          </div>
        </Section>
      )}

      {/* Next case study */}
      {next && next.slug !== slug && (
        <section className="relative py-16 border-t border-white/5" data-testid="cs-next">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="flex items-center justify-between gap-6 flex-wrap">
              <div>
                <p className="text-[11px] uppercase tracking-[0.25em] text-white/40 font-bold mb-2">Next case study</p>
                <p className="font-display text-2xl lg:text-3xl text-white">{next.title}</p>
              </div>
              <Link to={`/success-stories/${next.slug}`}
                className="group inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/15 text-white text-sm font-semibold hover:bg-white/5 transition-colors">
                Read next <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
};

export default CaseStudyPage;
